import React, { useState, useEffect, useMemo } from 'react';
import { ReptoolModal } from './ReptoolModal.tsx';
import { BasicFetch } from '../../utils/BasicFetch.ts';
import { News } from '../../types/News.ts';
import { Col, ListGroupItem, Row } from 'react-bootstrap';
import { Notification } from '../../types/Notifications.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faMessage, faNewspaper } from '@fortawesome/free-solid-svg-icons';
import { Carousel } from 'react-responsive-carousel';
import { useRepToolContext } from '../../RepToolContext.tsx';

const NavItem = ({ message, setActiveMessage }: {
  message: Notification | News,
  setActiveMessage: (message: Notification | News) => void,
}) => {
  const textColor = message.has_been_read ? 'secondary' : 'light';
  return (
    <ListGroupItem key={message.id} className='bg-dark mt-2 d-flex justify-content-between rounded' onClick={() => setActiveMessage(message)}>
      <div className='d-flex flex-column'>
        <h6 className={`text-${textColor}`}>{message.title}</h6>
        <small className='text-light mr-auto'>{new Date(message.created_timestamp).toDateString()}</small>
      </div>
      <div className={`text-right text-${textColor} my-auto`}>
        <FontAwesomeIcon size='xl' icon={message.type === "NEWS" ? faNewspaper : faMessage} />
      </div>
    </ListGroupItem>
  )
}


export const NewsModal = ({ closeModal, }: {
  closeModal: () => void,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [news, setNews] = useState<News[]>([]);
  const [activeMessage, setActiveMessage] = useState<Notification | News | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { appendControls } = useRepToolContext();

  const getMessages = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}news/get`,
      method: 'GET',
      onSuccess: ({ news }) => {
        setNews(news);
      }
    })
    BasicFetch({
      url: `${(window as any).serverLocation}notifications/get`,
      method: 'GET',
      onSuccess: ({ notifications }) => {
        setNotifications(notifications);
      }
    })
  }

  const markRead = (message: Notification | News) => {
    BasicFetch({
      url: `${(window as any).serverLocation}${message.type.toLowerCase()}/${message.id}/mark_read`,
      method: 'POST',
      onSuccess: () => {
        getMessages();
      }
    })
  }

  useEffect(() => {
    if (activeMessage && !(activeMessage as Notification).has_been_read) {
      markRead(activeMessage as Notification);
    }
  }, [activeMessage])

  useEffect(() => {
    getMessages();
    appendControls(null, 'News Modal');
  }, [])

  const messages = useMemo(() => {
    return [...news, ...notifications].sort((a, b) => {
      return a.created_timestamp > b.created_timestamp ? -1 : 1;
    })
  }, [news, notifications]);
  return (
    <ReptoolModal title="Messages" closeModal={() => closeModal()} body={
      <>
        <div className='d-none d-lg-block'>
          <Row>
            <Col md={3}>
              {
                messages.map(message => (
                  <NavItem key={message.id} message={message} setActiveMessage={setActiveMessage} />
                ))
              }
            </Col>
            <Col md={9}>
              {
                activeMessage &&
                <div className='text-light'>
                  <h3 className='border-bottom pb-2'>{activeMessage.title}</h3>
                  <div className='p-3'>
                    <p style={{ whiteSpace: "pre-line" }}>{activeMessage.body}</p>
                    {activeMessage?.sending_user && <p>
                      Sent by: {activeMessage?.sending_user || 'Unknown'}
                    </p>}
                  </div>

                </div>
              }
            </Col>
          </Row>
        </div>
        <div className='d-lg-none'>
          <Carousel
            selectedItem={activeIndex}
            onChange={index => setActiveIndex(index)}
            showArrows={false}
            showStatus={false}
            showThumbs={false}
            showIndicators={false}
            swipeable={false}
          >
            <div>
              {
                messages.map(message => (
                  <NavItem key={message.id} message={message} setActiveMessage={() => {
                    setActiveMessage(message);
                    setActiveIndex(1);
                  }} />
                ))
              }
            </div>
            <div className='overflow-auto' style={{
              maxHeight: 'calc(100vh - 80px)',
            }}>
              <div className='d-flex flex-row justify-content-between border-bottom mb-4'>
                <FontAwesomeIcon size='lg' icon={faChevronLeft} onClick={() => { setActiveIndex(0) }} className='mr-3 my-auto' />
                <h3 className='flex-fill text-center'>{activeMessage?.title || ''}</h3>
              </div>
              <div className='p-3'>
                <p className='pb-4 text-left' style={{ whiteSpace: "pre-line" }}>{activeMessage?.body || ''}</p>
                {activeMessage?.sending_user && <p>
                  Sent by: {activeMessage?.sending_user || 'Unknown'}
                </p>}
              </div>

            </div>
          </Carousel>
        </div>
      </>

    } />
  )
}